import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtINR, fmtDateTime, fileUrl } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { roleLabel } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Paperclip, Trash } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { StatusPill } from "./Travel";

const newItem = () => ({ name: "", price: 0, quantity: 1, description: "" });

export default function ProjectExpenses() {
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("pending");
  const empty = { expense_name: "", account_id: "", opportunity_id: "", items: [newItem()], attachment_ids: [], remarks: "" };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");

  const load = () => api.get("/project-expenses").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/opportunities").then((r) => setOpps(r.data));
  }, []);

  const filteredOpps = useMemo(() => opps.filter((o) => !form.account_id || o.account_id === form.account_id), [form.account_id, opps]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("category", "project_expense");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      setForm((f) => ({ ...f, attachment_ids: [...f.attachment_ids, ...ids] }));
      toast.success(`${ids.length} file(s) attached`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const removeAtt = (fid) => setForm((f) => ({ ...f, attachment_ids: f.attachment_ids.filter((x) => x !== fid) }));

  const updateItem = (idx, key, val) => {
    const arr = [...form.items]; arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, items: arr });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, newItem()] });
  const removeItem = (idx) => {
    if (form.items.length === 1) return toast.error("At least one item is required");
    const arr = [...form.items]; arr.splice(idx, 1); setForm({ ...form, items: arr });
  };

  const total = form.items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);

  const save = async () => {
    if (!form.expense_name.trim()) return toast.error("Expense name required");
    if (!form.account_id) return toast.error("Pick an account");
    if (!form.opportunity_id) return toast.error("Pick an opportunity");
    if (form.items.some((i) => !i.name.trim() || !Number(i.price))) return toast.error("Each item needs a name & price");
    try {
      const body = { ...form, items: form.items.map((i) => ({ ...i, price: Number(i.price), quantity: Number(i.quantity) || 1 })) };
      await api.post("/project-expenses", body);
      toast.success("Submitted"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const act = async (action) => {
    if (!comment.trim()) return toast.error("Comment is required");
    try { await api.post(`/project-expenses/${detail.id}/action`, { action, comment }); toast.success("Recorded"); setDetail(null); setComment(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const markPaid = async () => {
    if (!window.confirm("Mark this project expense as paid?")) return;
    try { await api.post(`/project-expenses/${detail.id}/mark-paid`); toast.success("Marked paid"); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (row) => {
    if (!window.confirm("Delete this project expense?")) return;
    try { await api.delete(`/project-expenses/${row.id}`); toast.success("Deleted"); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = items.filter((e) =>
    tab === "pending" ? e.status === "pending" :
    tab === "approved" ? e.status === "approved" :
    tab === "rejected" ? e.status === "rejected" :
    tab === "paid" ? e.status === "paid" : true
  );
  const counts = {
    pending: items.filter((e) => e.status === "pending").length,
    approved: items.filter((e) => e.status === "approved").length,
    rejected: items.filter((e) => e.status === "rejected").length,
    paid: items.filter((e) => e.status === "paid").length,
  };

  return (
    <div data-testid="project-expenses-page">
      <PageHeader title="Project Expenses" description="Book expenses against a customer opportunity — Finance Manager → Sales Manager → Super Admin approval chain." action={
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild><Button className="btn-brand" data-testid="pe-new"><Plus size={16} className="mr-1.5" /> New Project Expense</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Project Expense</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Field label="Expense Name *"><Input value={form.expense_name} onChange={(e) => setForm({ ...form, expense_name: e.target.value })} placeholder="e.g. Vendor materials for Site X" data-testid="pe-name" /></Field></div>
              <Field label="Account *">
                <Select value={form.account_id || ""} onValueChange={(v) => setForm({ ...form, account_id: v, opportunity_id: "" })}>
                  <SelectTrigger data-testid="pe-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Opportunity *">
                <Select value={form.opportunity_id || ""} onValueChange={(v) => setForm({ ...form, opportunity_id: v })} disabled={!form.account_id}>
                  <SelectTrigger data-testid="pe-opp"><SelectValue placeholder={form.account_id ? "Pick opportunity" : "Select account first"} /></SelectTrigger>
                  <SelectContent>{filteredOpps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">Items ({form.items.length})</h4>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="pe-add-item"><Plus size={14} className="mr-1" /> Add item</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center border rounded-md p-2" style={{ borderColor: "hsl(var(--border))" }} data-testid={`pe-item-${idx}`}>
                    <Input className="col-span-5" placeholder="Item name" value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="Price" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} />
                    <Input className="col-span-1" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                    <Input className="col-span-3" placeholder="Note" value={it.description || ""} onChange={(e) => updateItem(idx, "description", e.target.value)} />
                    <Button size="icon" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeItem(idx)}><Trash size={14} /></Button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between border-t pt-3" style={{ borderColor: "hsl(var(--border))" }}>
                <span className="text-sm opacity-70">Total items: {form.items.length}</span>
                <span className="text-sm">Grand Total: <b className="text-[#fd7e14] text-lg">{fmtINR(total)}</b></span>
              </div>

              <div className="mt-4">
                <Field label="Attachments (multiple)">
                  <Input type="file" multiple onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="pe-files" />
                  {uploading && <div className="text-xs opacity-60 mt-1">Uploading…</div>}
                  {form.attachment_ids.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2 text-xs">
                      {form.attachment_ids.map((fid) => (
                        <li key={fid} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                          <Paperclip size={12} className="text-[#fd7e14]" />
                          <a href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">{fid.slice(0, 6)}…</a>
                          <button type="button" onClick={() => removeAtt(fid)} className="text-red-600 ml-1">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
              </div>
              <div className="mt-3"><Field label="Remarks"><Textarea value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} /></Field></div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="btn-brand" onClick={save} data-testid="pe-save">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="pe-tab-pending">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="pe-tab-approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="pe-tab-rejected">Rejected ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="pe-tab-paid">Paid ({counts.paid})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Expense</TableHead><TableHead>Account / Opp.</TableHead><TableHead>By</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => { setDetail(r); setComment(""); }}>
                    <TableCell className="font-medium">{r.expense_name}</TableCell>
                    <TableCell className="text-sm"><div>{r.account_name}</div><div className="text-xs opacity-60 truncate max-w-[180px]">{r.opportunity_name}</div></TableCell>
                    <TableCell className="text-xs"><div className="capitalize">{roleLabel(r.applicant_role)}</div><div className="opacity-70">{r.applicant_name}</div></TableCell>
                    <TableCell className="text-xs">{(r.items || []).length}</TableCell>
                    <TableCell className="font-semibold">{fmtINR(r.total_amount)}</TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {r.can_act && <span className="text-[10px] uppercase tracking-wider font-semibold text-[#fd7e14]">Action Required</span>}
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm opacity-60 py-10">No project expenses.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.expense_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="opacity-60">Account:</span> <b>{detail.account_name}</b></div>
                <div><span className="opacity-60">Opportunity:</span> <b>{detail.opportunity_name}</b></div>
                <div><span className="opacity-60">Filed by:</span> {detail.applicant_name} <span className="opacity-60 capitalize">({roleLabel(detail.applicant_role)})</span></div>
                <div><span className="opacity-60">Status:</span> <StatusPill status={detail.status} /></div>
                <div><span className="opacity-60">Filed on:</span> {fmtDateTime(detail.created_at)}</div>
                <div><span className="opacity-60">Total:</span> <b className="text-[#fd7e14]">{fmtINR(detail.total_amount)}</b></div>
              </div>
              <div className="rounded-md border p-3" style={{ borderColor: "hsl(var(--border))" }}>
                <div className="text-xs uppercase opacity-60 mb-1">Items</div>
                <ul className="space-y-1">
                  {(detail.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span>{it.name} <span className="opacity-60">× {it.quantity}</span>{it.description ? ` — ${it.description}` : ""}</span>
                      <span className="font-medium">{fmtINR(Number(it.price) * (Number(it.quantity) || 1))}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {(detail.attachment_ids || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase opacity-60 mb-1">Attachments</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {detail.attachment_ids.map((fid, i) => (
                      <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">📎 View #{i + 1}</a>
                    ))}
                  </div>
                </div>
              )}
              {detail.remarks && <div className="text-xs"><span className="opacity-60">Remarks:</span> {detail.remarks}</div>}
              <div className="rounded-md border p-3 bg-slate-50 dark:bg-slate-800/30" style={{ borderColor: "hsl(var(--border))" }}>
                <div className="text-xs uppercase opacity-70 mb-1">Approval Trail</div>
                {(detail.actions || []).length === 0 && <div className="text-xs opacity-60">No actions yet.</div>}
                {(detail.actions || []).map((a, i) => (
                  <div key={i} className="text-xs mb-1">
                    <b>{a.actor_name}</b> ({a.role}) — <span className="capitalize">{a.action}</span>
                    <div className="opacity-70">{a.comment} • {fmtDateTime(a.timestamp)}</div>
                  </div>
                ))}
              </div>

              {(detail.can_act || (isSuper && detail.status === "approved")) && (
                <Textarea placeholder="Comment (required)…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="pe-comment" />
              )}

              <DialogFooter className="flex-wrap gap-2">
                {detail.can_act && <>
                  <Button variant="outline" className="text-red-600" onClick={() => act("reject")} data-testid="pe-reject">Reject</Button>
                  <Button className="btn-brand" onClick={() => act("approve")} data-testid="pe-approve">Approve</Button>
                </>}
                {!detail.can_act && detail.status === "approved" && isSuper && <Button className="btn-brand" onClick={markPaid} data-testid="pe-mark-paid">Mark as Paid</Button>}
                {isSuper && <Button variant="outline" className="text-red-600" onClick={() => remove(detail)} data-testid="pe-delete">Delete</Button>}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
